import { Plus } from 'lucide-react';
import type { HTMLAttributes } from 'react';

import { Badge } from '@/components/Badge/Badge';
import Button from '@/components/Button/Button';
import Spinner from '@/components/Common/Spinner';
import { ToggleStage } from '@/components/Stage/ToggleStage';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import { cn } from '@/lib/cn';
import { popup } from '@/stores/pure/PopupStore';
import {
  getFeedbackSourceName,
  getSourceIcon,
  type SOURCE_TYPE,
} from '@/types/feedbackConst';
import { track } from '@/utils/track/track';

import {
  type BaseIntegrationInfoDTO,
  IMPORT_STATE,
} from '../../providers/types';
import { INTEGRATION_STATUS, IntegrationStatusMap } from '../../types';

type BaseConfig<T> = {
  connected: boolean;
  source: SOURCE_TYPE;
  description: string;
  additionalActions?: (info: T) => React.ReactNode;
  healthStatus?: INTEGRATION_STATUS;
  onDisconnect: (info: T) => void;
  onConnectClick: () => void;
};

type ProviderConfig<T> =
  | (BaseConfig<T> & {
      connected: false;
      multiInfoEnabled: boolean;
    })
  | (BaseConfig<T> & {
      multiInfoEnabled: true;
      infos: T[];
      connectedDisplay?: (info: T) => React.ReactNode;
      onAddInfoClick: () => void;
    })
  | (BaseConfig<T> & {
      connectedDisplay?: React.ReactNode;
      multiInfoEnabled: false;
      info: T;
    });

export const ProviderContainer = <T extends BaseIntegrationInfoDTO>(
  props: ProviderConfig<T>,
) => {
  const {
    multiInfoEnabled,
    onDisconnect,
    connected,
    description,
    additionalActions,
    onConnectClick,
    healthStatus,
  } = props;

  const icon = getSourceIcon(props.source, 24);
  const name = getFeedbackSourceName(props.source);

  const handleDisconnectClick = (info: T) => {
    popup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Are you sure you want to disconnect?',
      confirmText: 'Disconnect',
      onConfirm: () => onDisconnect(info),
    });
  };

  const handleConnectClick = () => {
    track(AMP_KEYS.INTEGRATION_ATTEMPT_START, {
      integration_type: String(props.source).toLowerCase(),
    });
    onConnectClick();
  };

  if (props.healthStatus === INTEGRATION_STATUS.DISABLE) return null;

  if (!connected)
    return (
      <ProviderWrapper onClick={handleConnectClick}>
        <ProviderBaseInfo icon={icon} name={name} status={healthStatus} />
        <div className="flex-1">
          <Typo type="body2" className="text-gray-800">
            {description}
          </Typo>
        </div>
      </ProviderWrapper>
    );

  if (!multiInfoEnabled)
    return (
      <ProviderWrapper>
        <ProviderBaseInfo icon={icon} name={name} status={healthStatus} />
        <div className="flex-1 flex justify-between items-center">
          <Typo type="body2" className="text-gray-800">
            {props.connectedDisplay}
          </Typo>
          <ToggleStage
            className="flex justify-end items-center gap-2"
            status={props.info.import_state !== IMPORT_STATE.IMPORTING}
            fallback={
              <div className="flex items-center gap-2">
                <Spinner />
                <Typo type="body2" className="text-gray-800">
                  Importing
                </Typo>
              </div>
            }
          >
            <div>{additionalActions?.(props.info)}</div>
            <Button
              variant="error_outlined"
              onClick={() => handleDisconnectClick(props.info)}
            >
              Disconnect
            </Button>
          </ToggleStage>
        </div>
      </ProviderWrapper>
    );
  return (
    <div className="flex flex-col gap-2">
      {props.infos.map((info) => (
        <ProviderWrapper key={info.alias}>
          <ProviderBaseInfo icon={icon} name={name} status={healthStatus} />
          <div className="flex-1 flex justify-between items-center">
            <Typo type="body2" className="text-gray-800">
              {props.connectedDisplay?.(info)}
            </Typo>
            <div className="flex gap-2 justify-end items-center">
              <div>{additionalActions?.(info)}</div>
              <Button
                variant="error_outlined"
                onClick={() => {
                  handleDisconnectClick(info);
                }}
              >
                Disconnect
              </Button>
            </div>
          </div>
        </ProviderWrapper>
      ))}
      {props.infos.length > 0 && (
        <div className="flex justify-start items-center">
          <Button
            leftIcon={<Plus className="size-4" />}
            variant="ghost"
            className="text-muted-foreground"
            onClick={() => {
              track(AMP_KEYS.INTEGRATION_ATTEMPT_START, {
                integration_type: String(props.source).toLowerCase(),
              });
              props.onAddInfoClick();
            }}
          >
            Add more {name} integration
          </Button>
        </div>
      )}
    </div>
  );
};

const ProviderWrapper = (props: HTMLAttributes<HTMLDivElement>) => {
  const { className, ...rest } = props;
  return (
    <div
      className={cn(
        'flex px-6 py-3 border border-gray-400 cursor-pointer transition-all duration-200 rounded-md align-center justify-between gap-4',
        'hover:border-primary',
        className,
      )}
      {...rest}
    >
      {props.children}
    </div>
  );
};

const ProviderBaseInfo = ({
  icon,
  name,
  subtitle,
  status,
}: {
  icon: React.ReactNode;
  name: string;
  subtitle?: string;
  status?: INTEGRATION_STATUS;
}) => {
  return (
    <div className="flex items-center gap-4 justify-between">
      <div className="flex items-center gap-4 w-60">
        {icon}
        <div className="flex items-center gap-2">
          <Typo type="label2" weight="sb">
            {name}
          </Typo>
          {!!subtitle && (
            <span className="text-sm text-gray-700">{subtitle}</span>
          )}
        </div>
      </div>
      {status && status !== INTEGRATION_STATUS.HEALTHY && (
        <Badge variant="warning">{IntegrationStatusMap.get(status)}</Badge>
      )}
    </div>
  );
};
